(() => {
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  class ModelSwipe extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.stage = this.querySelector('.em-model__stage');
      this.items = [...this.querySelectorAll('.em-model__garment')];
      this.index = 0;
      this.spacing = Number(this.dataset.spacing) || 70;
      this.suppressUntil = 0;
      this.modelToggle = this.querySelector('[data-model-toggle]');
      this.items.forEach(item => {
        item.maleFit ||= Object.fromEntries(['x', 'y', 'width', 'angle'].map(key => [key, item.style.getPropertyValue(`--em-fit-${key}`)]));
      });
      this.setModel('male');
      this.modelToggle?.addEventListener('click', () => this.setModel(this.dataset.activeModel === 'female' ? 'male' : 'female'), opts);
      this.classList.add('is-ready');
      this.querySelectorAll('[data-step]').forEach(b => b.addEventListener('click', () => this.select(this.index + Number(b.dataset.step)), opts));
      this.items.forEach((item, i) => item.querySelector('button').addEventListener('click', e => {
        if (performance.now() < this.suppressUntil) { e.preventDefault(); return; }
        this.select(i);
      }, opts));
      this.stage.addEventListener('keydown', e => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();
        this.select(e.key === 'Home' ? 0 : e.key === 'End' ? this.items.length - 1 : this.index + (e.key === 'ArrowRight' ? 1 : -1));
        this.stage.focus({ preventScroll: true });
      }, opts);
      this.stage.addEventListener('pointerdown', e => {
        if (e.pointerType === 'touch' || e.button !== 0 || !e.isPrimary) return;
        this.beginDrag(e.pointerId, e.clientX, e.clientY, e.timeStamp, 'pointer');
      }, opts);
      window.addEventListener('pointermove', e => {
        if (this.drag?.kind !== 'pointer' || this.drag.id !== e.pointerId) return;
        if (this.moveDrag(e.clientX, e.clientY) && e.cancelable) e.preventDefault();
      }, opts);
      window.addEventListener('pointerup', e => {
        if (this.drag?.kind !== 'pointer' || this.drag.id !== e.pointerId) return;
        this.moveDrag(e.clientX, e.clientY);
        this.finishDrag(e.timeStamp);
      }, opts);
      window.addEventListener('pointercancel', e => {
        if (this.drag?.kind === 'pointer' && this.drag.id === e.pointerId) this.finishDrag(e.timeStamp, true);
      }, opts);
      this.stage.addEventListener('touchstart', e => {
        if (e.touches.length !== 1) { this.finishDrag(e.timeStamp, true); return; }
        const touch = e.changedTouches[0];
        this.beginDrag(touch.identifier, touch.clientX, touch.clientY, e.timeStamp, 'touch');
      }, { ...opts, passive: true });
      this.stage.addEventListener('touchmove', e => {
        if (this.drag?.kind !== 'touch') return;
        const touch = [...e.touches].find(t => t.identifier === this.drag.id);
        if (touch && this.moveDrag(touch.clientX, touch.clientY) && e.cancelable) e.preventDefault();
      }, { ...opts, passive: false });
      window.addEventListener('touchend', e => {
        if (this.drag?.kind !== 'touch') return;
        const touch = [...e.changedTouches].find(t => t.identifier === this.drag.id);
        if (!touch) return;
        this.moveDrag(touch.clientX, touch.clientY);
        this.finishDrag(e.timeStamp);
      }, { ...opts, passive: true });
      window.addEventListener('touchcancel', e => {
        if (this.drag?.kind === 'touch') this.finishDrag(e.timeStamp, true);
      }, { ...opts, passive: true });
      this.stage.addEventListener('click', e => {
        if (!this.suppressClick) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.suppressClick = false;
      }, { ...opts, capture: true });
      this.stage.addEventListener('dragstart', e => e.preventDefault(), opts);
      document.addEventListener('shopify:block:select', e => {
        const i = this.items.findIndex(item => item.dataset.blockId === e.detail?.blockId);
        if (i >= 0) this.select(i);
      }, opts);
      this.select(0, false);
    }
    beginDrag(id, x, y, time, kind) {
      if (this.items.length < 2) return;
      this.suppressClick = false;
      this.suppressUntil = 0;
      this.drag = { id, x, y, time, kind, index: this.index, moved: false, dx: 0, position: this.index };
    }
    moveDrag(x, y) {
      const d = this.drag;
      if (!d) return false;
      const dx = x - d.x, dy = y - d.y;
      if (!d.moved) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { this.drag = null; return false; }
        if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return false;
        d.moved = true;
        this.suppressClick = true;
        this.stage.classList.add('is-dragging');
      }
      const unit = this.stage.getBoundingClientRect().width * this.spacing / 100;
      d.dx = dx;
      d.position = clamp(d.index - dx / Math.max(1, unit), 0, this.items.length - 1);
      this.layout(d.position);
      return true;
    }
    finishDrag(time, cancelled = false) {
      const d = this.drag;
      if (!d) return;
      this.drag = null;
      this.stage.classList.remove('is-dragging');
      if (!d.moved) return;
      this.suppressUntil = performance.now() + 500;
      const velocity = Math.abs(d.dx) / Math.max(1, time - d.time);
      const advance = Math.abs(d.dx) >= 20 || (Math.abs(d.dx) >= 12 && velocity > .35);
      let target = Math.round(d.position);
      if (advance && target === d.index) target += d.dx < 0 ? 1 : -1;
      this.select(cancelled ? d.index : target);
    }
    setModel(model) {
      if (model === 'female' && !this.querySelector('[data-model-view="female"]')) return;
      this.dataset.activeModel = model;
      this.querySelectorAll('[data-model-view]').forEach(view => { view.hidden = view.dataset.modelView !== model; });
      this.items.forEach(item => {
        ['x', 'y', 'width', 'angle'].forEach(key => {
          const femaleValue = item.dataset[`female${key[0].toUpperCase()}${key.slice(1)}`];
          const value = model === 'female' && item.dataset.femaleFit === 'true' ? `${femaleValue}${key === 'angle' ? 'deg' : '%'}` : item.maleFit[key];
          item.style.setProperty(`--em-fit-${key}`, value);
        });
      });
      if (this.modelToggle) {
        const female = model === 'female';
        this.modelToggle.textContent = female ? this.modelToggle.dataset.maleLabel : this.modelToggle.dataset.femaleLabel;
        this.modelToggle.setAttribute('aria-label', female ? this.modelToggle.dataset.maleAction : this.modelToggle.dataset.femaleAction);
        this.modelToggle.setAttribute('aria-pressed', String(female));
      }
    }
    layout(position) {
      this.items.forEach((item, i) => {
        const distance = Math.abs(i - position);
        item.style.setProperty('--em-shift', (i - position) * this.spacing);
        const side = Number(this.style.getPropertyValue('--em-side-opacity')) || .25;
        item.style.setProperty('--em-opacity', distance > 2.1 ? 0 : side + (1 - side) * Math.max(0, 1 - distance));
        item.style.visibility = distance > 2.1 ? 'hidden' : 'visible';
        item.setAttribute('aria-hidden', distance > 2.1 ? 'true' : 'false');
      });
    }
    select(index, announce = true) {
      if (!this.items.length) { this.querySelector('.em-model__footer').hidden = true; return; }
      this.index = clamp(index, 0, this.items.length - 1);
      this.layout(this.index);
      this.items.forEach((item, i) => {
        item.classList.toggle('is-selected', i === this.index);
        const b = item.querySelector('button');
        b.setAttribute('aria-pressed', String(i === this.index));
        b.tabIndex = i === this.index ? 0 : -1;
      });
      const item = this.items[this.index];
      this.querySelector('.em-model__footer [data-title]').textContent = item.dataset.title || '';
      const price = this.querySelector('.em-model__footer [data-price]');
      if (price) price.textContent = item.dataset.price || '';
      const link = this.querySelector('[data-shop]');
      link.hidden = !item.dataset.url;
      if (item.dataset.url) link.href = item.dataset.url;
      this.querySelector('[data-count]').textContent = `${this.index + 1} / ${this.items.length}`;
      this.querySelector('[data-step="-1"]').disabled = this.index === 0;
      this.querySelector('[data-step="1"]').disabled = this.index === this.items.length - 1;
      if (announce) this.querySelector('[data-announcement]').textContent = `${item.dataset.title || ''}, ${this.index + 1} / ${this.items.length}`;
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.drag = null; this.stage?.classList.remove('is-dragging'); }
  }
  class BrickImages extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.cards = [...this.querySelectorAll('.em-bricks__card')].map(card => ({ card, frame: card.querySelector('.em-bricks__frame'), image: card.querySelector('img'), layer: card.querySelector('.em-bricks__tiles'), animations: [], visible: false }));
      this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
        const c = this.cards.find(c => c.frame === entry.target);
        if (!c) return;
        c.visible = entry.isIntersecting;
        if (c.visible && (!c.played || this.dataset.repeat === 'true')) this.reveal(c);
      }), { threshold: .12 });
      this.resize = new ResizeObserver(entries => entries.forEach(entry => {
        const c = this.cards.find(c => c.frame === entry.target);
        if (c?.animations.length) this.reveal(c);
      }));
      this.cards.forEach(c => {
        c.image?.addEventListener('load', () => { if (c.visible) this.reveal(c); }, opts);
        this.observer.observe(c.frame);
        this.resize.observe(c.frame);
      });
      this.querySelector('[data-rebuild]')?.addEventListener('click', () => this.cards.forEach(c => this.reveal(c)), opts);
      document.addEventListener('shopify:block:select', e => {
        const c = this.cards.find(c => c.card.dataset.blockId === e.detail?.blockId);
        if (c) this.reveal(c);
      }, opts);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionQuery.addEventListener('change', () => { if (this.motionQuery.matches) this.cards.forEach(c => this.finish(c)); }, opts);
    }
    finish(c) {
      c.generation = (c.generation || 0) + 1;
      c.animations.forEach(a => a.cancel());
      c.animations = [];
      c.frame.classList.remove('is-building');
      c.layer.replaceChildren();
    }
    build(c) {
      this.finish(c);
      if (!c.image?.naturalWidth || reduced() || this.dataset.motion === 'false') return;
      const rect = c.frame.getBoundingClientRect(), width = rect.width, height = rect.height;
      if (!width || !height) return;
      const cols = clamp(Number(this.dataset.columns) || 4, 2, 8), rows = clamp(Number(this.dataset.rows) || 6, 2, 10);
      const scale = Math.max(width / c.image.naturalWidth, height / c.image.naturalHeight);
      const iw = c.image.naturalWidth * scale, ih = c.image.naturalHeight * scale;
      for (let row = 0; row < rows; row++) {
        let left = 0, col = 0;
        while (left < cols) {
          const cell = row % 2 && col === 0 ? .5 : Math.min(1, cols - left);
          const x = left * width / cols, y = row * height / rows;
          const tile = document.createElement('span');
          tile.className = 'em-bricks__tile';
          Object.assign(tile.style, { left: `${x}px`, top: `${y}px`, width: `${cell * width / cols + .35}px`, height: `${height / rows + .35}px`, backgroundImage: `url(${JSON.stringify(c.image.currentSrc || c.image.src)})`, backgroundSize: `${iw}px ${ih}px`, backgroundPosition: `${(width - iw) / 2 - x}px ${(height - ih) / 2 - y}px` });
          tile.dataset.order = (rows - 1 - row) * (cols + 1) + col;
          tile.dataset.row = row;
          tile.dataset.col = col;
          c.layer.append(tile);
          left += cell; col++;
        }
      }
    }
    reveal(c) {
      this.build(c);
      if (!c.layer.children.length || typeof c.layer.firstElementChild.animate !== 'function') return;
      c.played = true;
      c.frame.classList.add('is-building');
      const generation = c.generation;
      const travel = parseFloat(this.style.getPropertyValue('--em-travel')) || 80;
      const duration = parseFloat(this.style.getPropertyValue('--em-duration')) || 900;
      c.animations = [...c.layer.children].map(tile => {
        const sign = Number(tile.dataset.col) % 2 ? 1 : -1;
        return tile.animate([{ opacity: 0, transform: `translate3d(${sign * travel * .45}px,${travel}px,-140px) rotateY(${sign * 24}deg) rotateX(18deg) scale(.9)` }, { opacity: 1, transform: 'translate3d(0,0,0) rotateY(0) rotateX(0) scale(1)' }], { duration, delay: Number(tile.dataset.order) * Number(this.dataset.stagger || 20), easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'both' });
      });
      Promise.allSettled(c.animations.map(a => a.finished)).then(() => { if (c.generation === generation) this.finish(c); });
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.observer?.disconnect(); this.resize?.disconnect(); this.cards?.forEach(c => this.finish(c)); }
  }
  class JacketReveal extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.manual = false;
      this.failed = false;
      this.inView = false;
      this.targetTime = 0;
      this.button = this.querySelector('[data-play]');
      this.resetButton = this.querySelector('[data-reset]');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.configure();
      this.motionQuery.addEventListener('change', () => { this.pause(); this.manual = false; this.configure(); this.sync(); }, opts);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) { this.load(); this.sync(); } else this.pause();
      }, { rootMargin: '200px', threshold: 0 });
      this.observer.observe(this);
      this.video.addEventListener('loadedmetadata', () => {
        this.video.playbackRate = clamp(Number(this.dataset.speed) || 1, .5, 1.5);
        this.sync();
      }, opts);
      this.video.addEventListener('loadeddata', () => this.classList.add('is-video-ready'), opts);
      this.video.addEventListener('seeked', () => { this.classList.add('is-video-ready'); this.seek(); }, opts);
      this.video.addEventListener('playing', () => { this.classList.add('is-video-ready'); this.button.textContent = this.button.dataset.pauseLabel; }, opts);
      this.video.addEventListener('pause', () => { this.button.textContent = this.button.dataset.openLabel; }, opts);
      this.video.addEventListener('ended', () => { this.button.textContent = this.button.dataset.openLabel; }, opts);
      this.video.addEventListener('error', () => this.fail(), opts);
      const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); }); };
      window.addEventListener('scroll', () => { if (this.dataset.mode === 'scroll' && this.auto()) { this.manual = false; this.pause(); } schedule(); }, { ...opts, passive: true });
      window.addEventListener('resize', schedule, { ...opts, passive: true });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pause(); else this.sync(); }, opts);
      this.button.addEventListener('click', () => { if (!this.video.paused) this.pause(); else this.play(true); }, opts);
      this.resetButton.addEventListener('click', () => this.reset(), opts);
      this.querySelector('.em-reveal__visual').addEventListener('click', () => { if (this.video.paused) this.play(true); else this.pause(); }, opts);
      this.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse' && this.dataset.mode === 'hover' && this.auto()) this.play(false); }, opts);
      this.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse' && this.dataset.mode === 'hover' && !this.manual) this.reset(); }, opts);
      document.addEventListener('shopify:section:select', e => { if (e.target.contains(this)) this.reset(); }, opts);
    }
    auto() { return this.dataset.motion !== 'false' && !this.motionQuery.matches && this.dataset.designMode !== 'true'; }
    configure() { this.classList.toggle('is-scroll', this.dataset.mode === 'scroll' && this.auto()); }
    load() { if (!this.video.src && this.video.dataset.src && !this.failed) { this.video.src = this.video.dataset.src; this.video.load(); } }
    sync() {
      if (!this.video || this.failed || !this.inView || document.hidden || this.manual || !this.auto() || this.dataset.mode !== 'scroll') return;
      const rect = this.getBoundingClientRect();
      const distance = Math.max(1, this.offsetHeight - window.innerHeight);
      const progress = clamp(-rect.top / distance, 0, 1);
      if (Number.isFinite(this.video.duration) && this.video.duration > 0) {
        this.video.pause();
        this.targetTime = progress * Math.max(0, this.video.duration - .04);
        this.seek();
      }
    }
    seek() {
      if (this.video.seeking || !this.video.paused || this.manual || this.failed || this.dataset.mode !== 'scroll' || !Number.isFinite(this.video.duration)) return;
      if (Math.abs(this.video.currentTime - this.targetTime) > .035) this.video.currentTime = this.targetTime;
    }
    async play(manual) {
      if (this.failed || document.hidden) return;
      this.load();
      this.manual = manual;
      if (this.video.ended || (Number.isFinite(this.video.duration) && this.video.currentTime >= this.video.duration - .08)) this.video.currentTime = 0;
      try { await this.video.play(); } catch { this.pause(); }
    }
    pause() { this.video?.pause(); }
    reset() {
      this.pause();
      this.manual = true;
      this.targetTime = 0;
      if (this.video.readyState > 0) this.video.currentTime = 0;
      else this.classList.remove('is-video-ready');
      this.button.textContent = this.button.dataset.openLabel;
      if (this.dataset.mode === 'hover') this.manual = false;
    }
    fail() {
      this.failed = true;
      this.pause();
      this.classList.remove('is-video-ready', 'is-scroll');
      this.button.disabled = true;
      this.resetButton.disabled = true;
      this.querySelector('[data-status]').textContent = this.dataset.error || '';
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.observer?.disconnect(); cancelAnimationFrame(this.frame); this.frame = 0; this.pause(); }
  }
  if (!customElements.get('escala-model-swipe')) customElements.define('escala-model-swipe', ModelSwipe);
  if (!customElements.get('escala-brick-images')) customElements.define('escala-brick-images', BrickImages);
  if (!customElements.get('escala-jacket-reveal')) customElements.define('escala-jacket-reveal', JacketReveal);
})();
